import React, { useState, useEffect } from 'react';
import { X, ChevronRight, ChevronLeft, Bot, User } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

// Array of tour steps with their content
const tourSteps = [
  {
    title: "Welcome to JPRO!",
    description: "Let's take a quick tour of your new dashboard to help you get started.",
    icon: <Bot className="h-10 w-10 text-blue-500" />,
  },
  {
    title: "Chat with JPRO Assistant",
    description: "Ask questions about affordable housing, compliance, and tenant rights. Our specialized assistant is here to help. Upload images and documents for analysis, or use our multi-model chat for more complex queries.",
    icon: <Bot className="h-10 w-10 text-blue-500" />,
    link: "/dashboard/new-chat",
    linkText: "Start a Chat"
  },
  {
    title: "Complete Your Profile",
    description: "Update your profile information to personalize your experience and get the most out of JPRO.",
    icon: <User className="h-10 w-10 text-orange-500" />,
    link: "/dashboard/profile",
    linkText: "Update Profile"
  }
];

function WelcomeTour({ onClose }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [isClosed, setIsClosed] = useState(false);
  const { currentUser } = useAuth();
  
  // Check if this is a new user and if they've seen the tour
  useEffect(() => {
    const hasSeenTour = localStorage.getItem('jpro_tour_completed');
    const userCreatedAt = currentUser?.createdAt;
    
    // If user has seen the tour or is not a new user, close the tour
    if (hasSeenTour) {
      setIsClosed(true);
    }
  }, [currentUser]);
  
  const handleClose = () => {
    localStorage.setItem('jpro_tour_completed', 'true');
    setIsClosed(true);
    if (onClose) onClose();
  };
  
  const nextStep = () => {
    if (currentStep < tourSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleClose();
    }
  };
  
  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };
  
  // If tour is closed, don't render anything
  if (isClosed) return null;
  
  const step = tourSteps[currentStep];
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
        <div className="relative p-6">
          {/* Close button */}
          <button 
            onClick={handleClose}
            className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
          >
            <X size={20} />
          </button>
          
          {/* Step content */}
          <div className="text-center mb-6">
            <div className="flex justify-center mb-4">
              {step.icon}
            </div>
            <h2 className="text-xl font-dm-serif font-bold mb-2 text-gray-800 dark:text-white">
              {step.title}
            </h2>
            <p className="text-gray-600 dark:text-gray-300 font-rasa">
              {step.description}
            </p>
            
            {/* Optional link to feature */}
            {step.link && (
              <div className="mt-4">
                <Link
                  to={step.link}
                  className="inline-flex items-center px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white font-medium rounded-lg transition-colors"
                  onClick={handleClose}
                >
                  {step.linkText}
                </Link>
              </div>
            )}
          </div>
          
          {/* Progress indicator */}
          <div className="flex justify-center mb-4">
            {tourSteps.map((_, index) => (
              <div 
                key={index}
                className={`h-2 w-2 rounded-full mx-1 ${
                  index === currentStep 
                    ? 'bg-blue-500' 
                    : 'bg-gray-300 dark:bg-gray-600'
                }`}
              />
            ))}
          </div>
          
          {/* Navigation buttons */}
          <div className="flex justify-between">
            <button
              onClick={prevStep}
              className={`flex items-center px-3 py-1 rounded ${
                currentStep === 0
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'text-blue-500 hover:bg-blue-50 dark:hover:bg-gray-700'
              }`}
              disabled={currentStep === 0}
            >
              <ChevronLeft size={16} className="mr-1" />
              Back
            </button>
            
            <button
              onClick={nextStep}
              className="flex items-center px-3 py-1 text-blue-500 hover:bg-blue-50 dark:hover:bg-gray-700 rounded"
            >
              {currentStep === tourSteps.length - 1 ? 'Finish' : 'Next'}
              {currentStep < tourSteps.length - 1 && <ChevronRight size={16} className="ml-1" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default WelcomeTour;